import { useEffect, useMemo, useState } from 'react';
import type { CharacterSlotId, ModuleRendererProps, ServerEventMessage } from '@momentum/shared';
import { z } from 'zod';
import type { ModuleRenderer } from '../renderer.ts';
import { CharacterBadge } from '../board/CharacterBadge.tsx';
import { useDeadline } from '../board/useDeadline.ts';
import { FixtureTeam2v2View } from './manifest.ts';

const TeamSubmittedPayload = z.object({ teamId: z.string(), solved: z.boolean() });

function teamStatusText(team: FixtureTeam2v2View['teams'][number]): string {
  if (!team.submitted) return 'Not submitted yet';
  if (team.solved === null) return 'Submitted';
  return team.solved ? 'Submitted — solved' : 'Submitted — incorrect';
}

function FixtureTeam2v2Renderer({ instance, session, you, send, onEvent, serverTimeOffset, readOnly }: ModuleRendererProps) {
  const view = useMemo(() => FixtureTeam2v2View.parse(instance.view), [instance.view]);
  const youId = you?.participantId ?? null;
  const incident = useDeadline(instance.deadlines, 'incident', serverTimeOffset);

  const participantsById = useMemo(() => new Map(session.participants.map((p) => [p.id, p])), [session.participants]);
  const nameOf = (id: string): string => (id === youId ? 'You' : (participantsById.get(id)?.displayName ?? id));
  const slotOf = (id: string): CharacterSlotId | null => participantsById.get(id)?.characterSlot ?? null;

  const [code, setCode] = useState('');
  const [pending, setPending] = useState(false);
  const [reject, setReject] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    return onEvent((event: ServerEventMessage) => {
      if (event.name !== 'team.submitted') return;
      const parsed = TeamSubmittedPayload.safeParse(event.payload);
      if (!parsed.success) return;
      // `team.submitted` is scoped server-side to the submitting team, so receiving it at all means it's ours.
      setToast(parsed.data.solved ? 'Your team submitted — solved!' : 'Your team submitted — incorrect.');
    });
  }, [onEvent]);

  async function submit(): Promise<void> {
    if (!code.trim()) return;
    setPending(true);
    const outcome = await send('submit', { code: code.trim() });
    setPending(false);
    setReject(outcome.ok ? '' : outcome.message || outcome.code);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4, 16px)' }}>
      <p role="status" style={{ margin: 0 }}>
        {view.phase === 'active'
          ? `Incident window · ${incident.remainingMs === null ? '—' : Math.ceil(incident.remainingMs / 1000)}s${incident.paused ? ' (paused)' : ''}`
          : 'Incident resolved'}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        {view.teams.map((team) => (
          <div
            key={team.teamId}
            style={{
              border: '1px solid var(--line, #e2e5ea)',
              borderRadius: 'var(--radius-card, 16px)',
              padding: 12,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              background: team.teamId === view.myTeam?.teamId ? 'var(--accent-soft, #e8f0ff)' : 'var(--surface, #f6f7f9)',
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>{team.label}</p>
            <div style={{ display: 'flex', gap: 6 }}>
              {team.participantIds.map((pid) => (
                <CharacterBadge key={pid} slot={slotOf(pid)} size={28} title={nameOf(pid)} emphasis={pid === youId ? 'you' : 'none'} />
              ))}
            </div>
            <p style={{ margin: 0 }}>{teamStatusText(team)}</p>
          </div>
        ))}
      </div>

      {view.myTeam && (
        <div
          style={{
            border: '1px dashed var(--accent, #1a73ff)',
            borderRadius: 'var(--radius-card, 16px)',
            padding: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <p style={{ margin: 0, fontWeight: 600, color: 'var(--accent, #1a73ff)' }}>Team-private</p>
          <p style={{ margin: 0 }}>
            Your team's fragment: <strong style={{ fontFamily: 'monospace', fontSize: '1.1em' }}>{view.myTeam.fragment}</strong>
          </p>
          {!readOnly && (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void submit();
              }}
              style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
            >
              <label>
                <span style={{ display: 'block', marginBottom: 4 }}>Code</span>
                <input
                  type="text"
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  disabled={!view.myTeam.canSubmit || pending}
                  maxLength={16}
                  style={{ minHeight: 'var(--touch-target, 44px)', padding: '0 8px', borderRadius: 'var(--radius-control, 12px)' }}
                />
              </label>
              <button
                type="submit"
                disabled={!view.myTeam.canSubmit || pending || !code.trim()}
                style={{ minHeight: 'var(--touch-target, 44px)', padding: '0 16px', borderRadius: 'var(--radius-control, 12px)' }}
              >
                {view.myTeam.canSubmit ? 'Submit' : 'Submitted'}
              </button>
            </form>
          )}
          {reject && (
            <p role="alert" style={{ color: 'var(--danger, #d64545)', margin: 0 }}>
              {reject}
            </p>
          )}
        </div>
      )}

      <div aria-live="polite" style={{ minHeight: '1.2em', fontSize: '0.9em' }}>{toast ?? ''}</div>

      {view.phase === 'complete' && (
        <div>
          <h3 style={{ margin: '0 0 4px' }}>Result</h3>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {view.teams.map((team) => (
              <li key={team.teamId}>
                {team.label}: {team.solved ? 'Solved' : 'Not solved'}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export const fixtureTeam2v2ClientRenderer: ModuleRenderer = {
  renderer: 'fixture-team-2v2',
  Component: FixtureTeam2v2Renderer,
};
